import { MODEL_ID_REGEX, maxStringLength } from "../model-constants";
import type { IProcessOptionalString } from "../process-things";
import type { nil } from "../type-utils";
import { ProcessModelCopyMatchReason } from "./process-model-copy.model";
import { isEmpty, uniq } from "lodash";

/** Something a source element or role can be matched to: a target element by modelId, or a target role by key. */
export interface LabelledCandidate {
  key: string;
  displayName?: IProcessOptionalString[];
}

/** How a source name matched the candidates: one (the suggestion), several (the user picks) or none. */
export interface LabelMatch {
  reason:
    | ProcessModelCopyMatchReason.SameLabel
    | ProcessModelCopyMatchReason.AmbiguousLabel
    | ProcessModelCopyMatchReason.NoMatch;
  keys: string[];
}

const NO_MATCH: LabelMatch = { reason: ProcessModelCopyMatchReason.NoMatch, keys: [] };

// Spelling variants of the same name, e.g. "Matching-Koordination" and "Matching Koordination"
const SEPARATOR_RUN = /[\s\-_]+/g;

/** A display name as it is compared: trimmed, case-insensitive, with spaces, hyphens and underscores alike. */
export function normalizeLabel(value: string | nil): string {
  return (value ?? "").trim().toLocaleLowerCase().replace(SEPARATOR_RUN, " ");
}

/**
 * The languages two models' names are compared in: those both models have, the target's root language first, since
 * that is the one every target element is named in.
 */
export function getMatchLanguages(
  targetRootLanguage: string,
  targetLanguages: readonly string[],
  sourceLanguages: readonly string[]
): string[] {
  const shared = (targetLanguages ?? []).filter((language) => (sourceLanguages ?? []).includes(language));
  return shared.includes(targetRootLanguage) ? uniq([targetRootLanguage, ...shared]) : shared;
}

/**
 * Matches a source name against the candidates' names, language by language in the given order. The first language
 * with any match decides: one match is the suggestion, several leave the choice to the user. A language the source is
 * not named in is skipped.
 */
export function findLabelMatch(
  displayName: IProcessOptionalString[] | nil,
  candidates: readonly LabelledCandidate[],
  languages: readonly string[]
): LabelMatch {
  for (const language of languages) {
    const sourceLabel = normalizeLabel(getValue(displayName, language));
    if (isEmpty(sourceLabel)) {
      continue;
    }

    const keys = uniq(
      candidates
        .filter((candidate) => normalizeLabel(getValue(candidate.displayName, language)) === sourceLabel)
        .map(({ key }) => key)
    );
    if (keys.length === 1) {
      return { reason: ProcessModelCopyMatchReason.SameLabel, keys };
    }

    if (keys.length > 1) {
      return { reason: ProcessModelCopyMatchReason.AmbiguousLabel, keys };
    }
  }

  return NO_MATCH;
}

/** Whether a modelId may be stored: not blank, not too long, none of the characters file names cannot hold. */
export function isValidModelId(modelId: string | nil): boolean {
  const trimmed = modelId?.trim();
  if (isEmpty(trimmed) || trimmed !== modelId) {
    return false;
  }

  return modelId.length <= maxStringLength && MODEL_ID_REGEX.test(modelId);
}

/** The first of `<modelId>-2`, `<modelId>-3`, … that is not taken. */
export function suggestFreeModelId(modelId: string, takenModelIds: ReadonlySet<string>): string {
  let suffix = 2;
  while (takenModelIds.has(withNumberSuffix(modelId, suffix))) {
    suffix++;
  }

  return withNumberSuffix(modelId, suffix);
}

/**
 * Shortens the base so the result stays within `maxStringLength`: otherwise the backend rejects the whole batch,
 * and users who cannot edit modelIds have no way to fix the suggestion.
 */
function withNumberSuffix(modelId: string, suffix: number): string {
  const ending = `-${suffix}`;
  return `${modelId.slice(0, maxStringLength - ending.length)}${ending}`;
}

function getValue(strings: IProcessOptionalString[] | nil, language: string): string | null {
  return (strings ?? []).find((entry) => entry.language === language)?.value ?? null;
}
